/**
 * Per-user preferences for the Match page — persisted in localStorage so they
 * survive reloads and cross-tab edits. When we add a server-side field, swap
 * the storage layer and keep the hook API stable.
 *
 * Stored preferences:
 *  - viewMode: "profile" (one card at a time) vs. "grid" (2-col browse).
 *  - ageFilterEnabled: whether to apply the age range filter at all.
 *  - ageRange: [min, max] clamped to AGE_MIN..AGE_MAX.
 */

import { useCallback, useEffect, useState } from "react";

export type MatchViewMode = "profile" | "grid";

export type MatchPreferences = {
  viewMode: MatchViewMode;
  ageFilterEnabled: boolean;
  ageRange: [number, number];
};

export const AGE_MIN = 18;
export const AGE_MAX = 99;

const STORAGE_KEY = "GF_MATCH_PREFERENCES";
const LEGACY_VIEW_MODE_KEY = "GF_MATCH_VIEW_MODE";

const DEFAULTS: MatchPreferences = {
  viewMode: "profile",
  ageFilterEnabled: false,
  ageRange: [AGE_MIN, AGE_MAX],
};

function clampAge(n: number): number {
  if (!Number.isFinite(n)) return AGE_MIN;
  return Math.max(AGE_MIN, Math.min(AGE_MAX, Math.round(n)));
}

function normalizeRange(lo: unknown, hi: unknown): [number, number] {
  const a = clampAge(typeof lo === "number" ? lo : AGE_MIN);
  const b = clampAge(typeof hi === "number" ? hi : AGE_MAX);
  return a <= b ? [a, b] : [b, a];
}

// A stored payload is split into a persisted slice (what the user explicitly
// chose in the modal) and the derived runtime `MatchPreferences` we hand back.
// The age range falls back to the caller-supplied default — typically the
// user's profile age preferences — whenever it hasn't been explicitly set in
// the modal, so the default tracks profile edits without overwriting choices
// the user has already made here.
type StoredPreferences = {
  viewMode?: MatchViewMode;
  ageFilterEnabled?: boolean;
  ageRange?: [number, number];
};

function readStored(): StoredPreferences {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as StoredPreferences;
    const legacy = window.localStorage.getItem(LEGACY_VIEW_MODE_KEY);
    if (legacy === "grid" || legacy === "profile") return { viewMode: legacy };
  } catch {
    // fall through
  }
  return {};
}

function resolve(
  stored: StoredPreferences,
  defaultAgeRange: [number, number]
): MatchPreferences {
  const viewMode: MatchViewMode =
    stored.viewMode === "grid" || stored.viewMode === "profile"
      ? stored.viewMode
      : DEFAULTS.viewMode;
  const ageRange = Array.isArray(stored.ageRange)
    ? normalizeRange(stored.ageRange[0], stored.ageRange[1])
    : normalizeRange(defaultAgeRange[0], defaultAgeRange[1]);
  return {
    viewMode,
    ageFilterEnabled: stored.ageFilterEnabled === true,
    ageRange,
  };
}

function write(stored: StoredPreferences) {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
}

export function useMatchPreferences(
  defaultAgeRange: [number, number] = [AGE_MIN, AGE_MAX]
): [MatchPreferences, (next: Partial<MatchPreferences>) => void] {
  const [stored, setStored] = useState<StoredPreferences>(readStored);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setStored(readStored());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const update = useCallback((patch: Partial<MatchPreferences>) => {
    setStored((prev) => {
      const next: StoredPreferences = { ...prev };
      if (patch.viewMode !== undefined) next.viewMode = patch.viewMode;
      if (patch.ageFilterEnabled !== undefined) {
        next.ageFilterEnabled = patch.ageFilterEnabled;
      }
      if (patch.ageRange !== undefined) {
        next.ageRange = normalizeRange(patch.ageRange[0], patch.ageRange[1]);
      }
      write(next);
      return next;
    });
  }, []);

  const prefs = resolve(stored, defaultAgeRange);
  return [prefs, update];
}
